import * as React from 'react'

import { ChevronDownIcon, PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import {
  AndOrOperator,
  IsNotOperator,
  LogicalOperator,
  isNotOperatorValues,
} from '~/client/src/shared/models/LogicOperation'
import Tag, { ITag } from '~/client/src/shared/models/Tag'
import ExpressionElement from '~/client/src/shared/utils/ExpressionParser/ExpressionElement'
import ExpressionParser from '~/client/src/shared/utils/ExpressionParser/ExpressionParser'

import CategoryLogicalOperator from './CategoryLogicalOperator'

// localization: translated

interface IProps {
  categoryOperator?: LogicalOperator
  isNotOperatorValue: IsNotOperator
  categoryNameAndIconElement: JSX.Element
  categoryElements: ExpressionElement[]
  noElementsTitle?: string

  onAddNewElement?: () => void
  onNotOperatorChange?: (operator: IsNotOperator) => void
  onTagRemove?: (tagId: string) => void
}

@observer
export default class CategorySelectorRow extends React.Component<IProps> {
  public render() {
    const {
      categoryOperator,
      categoryNameAndIconElement,
      isNotOperatorValue,
      categoryElements,
      onAddNewElement,
      onNotOperatorChange,
    } = this.props

    return (
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <CategoryLogicalOperator selectedOperator={categoryOperator} />
        <span className="tw:inline-flex tw:h-8 tw:shrink-0 tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-input tw:px-2 tw:text-app-dense tw:whitespace-nowrap tw:text-foreground">
          {categoryNameAndIconElement}
        </span>
        <CategoryLogicalOperator
          selectedOperator={isNotOperatorValue}
          logicOperatorsList={isNotOperatorValues}
          isSelectable={!!categoryElements?.length}
          onChange={onNotOperatorChange}
        />
        {this.renderCategoryElements()}
        <Button
          variant="primary-ghost"
          size="icon-sm"
          aria-label={Localization.translator.add}
          onClick={onAddNewElement}
        >
          <PlusIcon />
        </Button>
      </div>
    )
  }

  private renderCategoryElements(): JSX.Element | JSX.Element[] {
    const { categoryElements, noElementsTitle, onAddNewElement } = this.props

    if (!categoryElements?.length) {
      return (
        <Button variant="ghost" size="sm" onClick={onAddNewElement}>
          {noElementsTitle}
          <ChevronDownIcon data-icon="inline-end" />
        </Button>
      )
    }

    return categoryElements.map((element, index) => {
      if (ExpressionParser.isTagElement(element)) {
        return (
          <span key={index} className="tw:inline-flex tw:min-w-0">
            {this.renderTag(element as Tag)}
          </span>
        )
      }

      if (ExpressionParser.isLogicalOperationElement(element)) {
        return (
          <CategoryLogicalOperator
            key={index}
            selectedOperator={element as AndOrOperator}
          />
        )
      }

      return (
        <span key={index} className="tw:text-app-dense tw:text-foreground">
          {element as string}
        </span>
      )
    })
  }

  private renderTag(tag: ITag): JSX.Element {
    const tagName = tag.name || Localization.translator.unknownDeletedObject

    return (
      <SitemapAttributeTag
        dataObject={tag as LocationBase}
        shouldShowAsTag={true}
        isRemovable={true}
        onRemove={this.props.onTagRemove}
        contentContainerClassName="tw:truncate tw:py-0.5"
      >
        <span title={tagName} className="tw:text-app-dense">
          {tagName}
        </span>
      </SitemapAttributeTag>
    )
  }
}
